import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { enterWorkspaceAsPlatformAdmin } from '@/api/context/workspaceAccessApi';
import { useAuth } from '@/hooks/useAuth';
import { useHasPlatformPermission } from '@/hooks/useHasPlatformPermission';
import { useTenantQuery } from '@/hooks/useTenantQuery';
import { showErrorNotification } from '@/utils/notifications';
import { cn } from '@/lib/utils';
import { OverviewTab } from './OverviewTab';
import { ModulesTab } from './ModulesTab';
import { MembersTab } from './MembersTab';
import { ActivityTab } from './ActivityTab';
import { CustomDomainsTab } from './CustomDomainsTab';
import { AssistantTab } from './AssistantTab';

type DetailTabId = 'overview' | 'members' | 'activity' | 'modules' | 'customDomains' | 'assistant';

const TENANT_DETAIL_TABPANEL_ID = 'tenant-detail-tabpanel';

function buildTenantDetailTabId(tabId: DetailTabId): string {
  return 'tenant-detail-tab-' + tabId;
}

export function TenantDetailPage() {
  const { t } = useTranslation('modules');
  const { t: tCustomDomains } = useTranslation('customDomains');
  const navigate = useNavigate();
  const { switchWorkspace } = useAuth();
  const canOpenWorkspace = useHasPlatformPermission('workspace.access_all');
  const canConfigureAssistant = useHasPlatformPermission('assistant_configuration.read');
  const { tenantId } = useParams<{ tenantId: string }>();
  const resolvedTenantId = tenantId === undefined ? '' : tenantId;
  const [activeTab, setActiveTab] = useState<DetailTabId>('overview');
  const [isOpeningWorkspace, setIsOpeningWorkspace] = useState(false);
  const tenantQuery = useTenantQuery(resolvedTenantId);
  const tenant = tenantQuery.data;

  async function openWorkspace() {
    setIsOpeningWorkspace(true);
    try {
      await enterWorkspaceAsPlatformAdmin(resolvedTenantId);
      await switchWorkspace({ scopeCode: 'tenant', tenantId: resolvedTenantId });
      navigate(APPLICATION_ROUTES.workspaceDashboard);
    } catch (openWorkspaceError) {
      void openWorkspaceError;
      showErrorNotification(t('admin.openWorkspaceError'));
      setIsOpeningWorkspace(false);
    }
  }

  return (
    <div className="p-6 md:p-8">
      <Button
        variant="link"
        onClick={function goBack() {
          navigate(APPLICATION_ROUTES.adminPractitioners);
        }}
        className="flex items-center gap-1.5 text-sm text-zoomuted hover:text-zooprimary mb-4"
      >
        <Icon name="ArrowLeft" />
        {t('admin.back')}
      </Button>

      {tenantQuery.isLoading === true ? (
        <div className="py-16 flex justify-center">
          <LoadingSpinner className="w-7 h-7" />
        </div>
      ) : tenantQuery.isError === true || tenant === undefined ? (
        <QueryErrorState
          onRetry={function retry() {
            tenantQuery.refetch();
          }}
        />
      ) : (
        <>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-2xl bg-zoovpalelavender text-zooprimary flex items-center justify-center shrink-0">
              <Icon name={tenant.type === 'organization' ? 'Building' : 'PawPrint'} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-zooindigo truncate">{tenant.name}</h1>
                <Badge tone={tenant.status === 'suspended' ? 'warning' : 'success'} size="sm">
                  {tenant.status === 'suspended'
                    ? t('admin.statusSuspended')
                    : t('admin.statusActive')}
                </Badge>
              </div>
              <p className="text-[11px] text-zoomuted font-mono truncate">{tenant.code}</p>
            </div>
            {canOpenWorkspace === true ? (
              <Button
                onClick={openWorkspace}
                isLoading={isOpeningWorkspace}
                className="flex items-center gap-2 shrink-0"
              >
                {isOpeningWorkspace === true ? null : <Icon name="LogIn" />}
                {isOpeningWorkspace === true
                  ? t('admin.openingWorkspace')
                  : t('admin.openWorkspace')}
              </Button>
            ) : null}
          </div>

          <div
            role="tablist"
            aria-label={tenant.name}
            className="flex gap-1 border-b border-zooborder mb-6 overflow-x-auto"
          >
            <TabButton
              id={buildTenantDetailTabId('overview')}
              controlsId={TENANT_DETAIL_TABPANEL_ID}
              label={t('admin.tabOverview')}
              isActive={activeTab === 'overview'}
              onSelect={function selectOverview() {
                setActiveTab('overview');
              }}
            />
            <TabButton
              id={buildTenantDetailTabId('members')}
              controlsId={TENANT_DETAIL_TABPANEL_ID}
              label={t('admin.tabMembers')}
              isActive={activeTab === 'members'}
              onSelect={function selectMembers() {
                setActiveTab('members');
              }}
            />
            <TabButton
              id={buildTenantDetailTabId('activity')}
              controlsId={TENANT_DETAIL_TABPANEL_ID}
              label={t('admin.tabActivity')}
              isActive={activeTab === 'activity'}
              onSelect={function selectActivity() {
                setActiveTab('activity');
              }}
            />
            <TabButton
              id={buildTenantDetailTabId('modules')}
              controlsId={TENANT_DETAIL_TABPANEL_ID}
              label={t('admin.tabModules')}
              isActive={activeTab === 'modules'}
              onSelect={function selectModules() {
                setActiveTab('modules');
              }}
            />
            <TabButton
              id={buildTenantDetailTabId('customDomains')}
              controlsId={TENANT_DETAIL_TABPANEL_ID}
              label={tCustomDomains('admin.tabLabel')}
              isActive={activeTab === 'customDomains'}
              onSelect={function selectCustomDomains() {
                setActiveTab('customDomains');
              }}
            />
            {canConfigureAssistant === true ? (
              <TabButton
                id={buildTenantDetailTabId('assistant')}
                controlsId={TENANT_DETAIL_TABPANEL_ID}
                label={t('admin.tabAssistant')}
                isActive={activeTab === 'assistant'}
                onSelect={function selectAssistant() {
                  setActiveTab('assistant');
                }}
              />
            ) : null}
          </div>

          <div
            role="tabpanel"
            id={TENANT_DETAIL_TABPANEL_ID}
            aria-labelledby={buildTenantDetailTabId(activeTab)}
          >
            {activeTab === 'overview' ? (
              <OverviewTab tenant={tenant} />
            ) : activeTab === 'members' ? (
              <MembersTab tenantId={resolvedTenantId} />
            ) : activeTab === 'activity' ? (
              <ActivityTab tenantId={resolvedTenantId} />
            ) : activeTab === 'assistant' ? (
              <AssistantTab tenantId={resolvedTenantId} tenantName={tenant.name} />
            ) : activeTab === 'modules' ? (
              <ModulesTab tenantId={resolvedTenantId} />
            ) : (
              <CustomDomainsTab tenantId={resolvedTenantId} />
            )}
          </div>
        </>
      )}
    </div>
  );
}

interface TabButtonProps {
  id: string;
  controlsId: string;
  label: string;
  isActive: boolean;
  onSelect: () => void;
}

function TabButton({ id, controlsId, label, isActive, onSelect }: TabButtonProps) {
  return (
    <button
      type="button"
      role="tab"
      id={id}
      aria-selected={isActive}
      aria-controls={controlsId}
      onClick={onSelect}
      className={cn(
        'px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap transition-colors',
        isActive === true
          ? 'border-zooprimary text-zooprimary'
          : 'border-transparent text-zoomuted hover:text-zooindigo'
      )}
    >
      {label}
    </button>
  );
}
